import { supabase } from "@/integrations/supabase/client";

export const createAuthToken = async (
  token: string, 
  firstMeetingDate: Date, 
  themeMeetingDate: Date,
  groupId?: string
) => {
  try {
    // Get current user
    const { data: { session } } = await supabase.auth.getSession();
    
    // Get the first survey ID (initial survey)
    const { data: surveyData, error: surveyError } = await supabase
      .from("surveys")
      .select("id")
      .eq("id", "c3d4e5f6-a7b8-9012-3456-cdefab123456") // Use the specific survey ID for first visit
      .maybeSingle();

    if (surveyError) throw surveyError;
    if (!surveyData) throw new Error("Initial survey not found");
    
    // Insert token with optional group assignment
    const insertData: {
      token: string;
      survey_id: string;
      first_meeting_date: string;
      theme_meeting_date: string;
      created_by: string | null;
      group_id?: string;
    } = {
      token,
      survey_id: surveyData.id,
      first_meeting_date: firstMeetingDate.toISOString(),
      theme_meeting_date: themeMeetingDate.toISOString(),
      created_by: session?.user?.id || null,
    };
    
    if (groupId) {
      insertData.group_id = groupId;
    }
    
    const { error: insertError } = await supabase
      .from("auth_tokens")
      .insert(insertData);

    if (insertError) throw insertError;
    
  } catch (error) {
    console.error("Error creating auth token:", error);
    throw error;
  }
};

// Function to assign a group after first survey completion
export const assignParticipantGroup = async (token: string): Promise<"intervention" | "control"> => {
  try {
    console.log(`Starting secure group assignment for token: ${token}`);
    
    // Use the secure function that handles token validation and group assignment
    // This function only returns necessary data and doesn't expose sensitive information
    const { data: result, error } = await supabase
      .rpc("validate_and_assign_group", { token_value: token });

    if (error) {
      console.error("Error in secure group assignment:", error);
      throw error;
    }

    // Type assertion for the expected return structure
    const validationResult = result as { valid: boolean; survey_id?: string; assigned_group?: "intervention" | "control" };

    if (!validationResult || !validationResult.valid) {
      console.error("Token validation failed:", token);
      throw new Error(`Invalid token: ${token}`);
    }

    if (!validationResult.assigned_group) {
      console.error("No group assigned:", token);
      throw new Error(`No group assigned for token: ${token}`);
    }

    console.log(`Successfully assigned/retrieved group for token ${token}:`, validationResult.assigned_group);
    
    return validationResult.assigned_group;
  } catch (error) {
    console.error("Error in secure group assignment:", error);
    throw error;
  }
};
